<template>
  <v-dialog
    v-model="visible"
    :fullscreen="fullscreen"
    :max-width="fullscreen ? '100%' : 800"
    :persistent="persistent"
  >
    <StandardCard
      @title-action-clicked="close"
      :title="title || undefined"
      :title-action="title ? closeTitleAction : undefined"
      card-text-class="mnh-250"
      border
      tile
    >
      <template
        v-if="!title && !persistent"
        #title
      >
        <div class="d-flex justify-end w-100">
          <StandardButton
            @click="close"
            aria-label="Close"
            color="grey"
            icon="close"
            size="small"
            variant="text"
          />
        </div>
      </template>

      <template v-if="loaded">
        <QuestionSet
          @back="backFromQuestion($event)"
          @change:attachments="loadAttachments"
          @next="forwardFromQuestion($event)"
          :attachment-group-id="groupId"
          :attachment-owner-id="form?.id"
          :attachment-owner-type="'Form'"
          :attachments="attachments"
          :model-value="form"
          :processing="processing"
          :questions="formQuestions"
          :readonly="form.locked"
          :schema="schema.definition"
          :section="section"
          :transition-name="transitionName"
          key-name="question"
        />
      </template>

      <template v-else>
        <div class="mnh-250 d-flex justify-center align-center">
          <v-progress-linear indeterminate />
        </div>
      </template>
    </StandardCard>
  </v-dialog>
</template>

<script setup>
import { useStore } from 'vuex';

import QuestionSet from '@/shared/components/form/QuestionSet.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useQuestionable from '@/shared/composables/useQuestionable';
import useRouterHelper from '@/shared/composables/useRouterHelper';
import useStepper from '@/shared/composables/useStepper';

defineExpose({ blankForm, isOpen, open });

const props = defineProps({
  afterClose: {
    type: Function,
    default: null,
  },
  afterSave: {
    type: Function,
    default: null,
  },
  draft: {
    type: Boolean,
    default: false,
  },
  enrollmentId: {
    type: String,
    default: null,
  },
  fullscreen: {
    type: Boolean,
    default: false,
  },
  groupId: {
    type: String,
    default: null,
  },
  persistent: {
    default: true,
    type: Boolean,
  },
  providerId: {
    type: String,
    default: null,
  },
  schemaId: {
    type: String,
    default: null,
  },
  showUnpublishedQuestions: {
    type: Boolean,
    default: false,
  },
  syncRouteQuery: {
    type: Boolean,
    default: true,
  },
  subsidyId: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['close', 'save']);

const { api } = useApi();
const eventBus = useEventBus();
const { updateQuery } = useRouterHelper();
const store = useStore();

async function syncFormRouteQuery(queryParams) {
  if (!props.syncRouteQuery) return;

  await updateQuery(queryParams);
}

const attachments = ref([]);
const form = ref(null);
const processing = ref(false);
const schema = ref(null);
const visible = ref(undefined);

watch(visible, (value) => {
  if (value === false) {
    void close();
  }
});

const role = computed(() => store.state.role);
const schemaId = computed(() => props.schemaId);

const closeTitleAction = computed(() => {
  if (props.persistent) return null;

  return { icon: 'close', variant: 'text', ariaLabel: 'Close' };
});

const {
  loadQuestions,
  questions,
  questionsLoaded,
  validQuestions,
  validPublishedQuestions,
  validateAnswers,
} = useQuestionable({
  ownerDataTypeRef: ref('Schema'),
  ownerIdRef: schemaId,
  syncedObjectRef: form,
});

const { section, transitionName } = useStepper({
  processing,
  updateQuery: syncFormRouteQuery,
});

const familyFacing = computed(() => {
  return schema.value?.meta?.family_facing?.enabled;
});

const loaded = computed(
  () => form.value && schema.value && questionsLoaded.value && questions.value?.length > 0,
);

let formQuestions = computed(() =>
  props.showUnpublishedQuestions ? validQuestions.value : validPublishedQuestions.value,
);

async function backFromQuestion(index) {
  processing.value = true;

  if (index - 1 < 0) {
    processing.value = false;
    await close();
  } else {
    processing.value = false;
    section.value = null;
    setTimeout(() => {
      section.value = `question-${index - 1}`;
    }, 600);
  }
}

function blankForm() {
  return {
    custom: {},
    enrollment_id: props.enrollmentId,
    group_id: props.groupId,
    provider_id: props.providerId,
    schema_id: props.schemaId,
    subsidy_id: props.subsidyId,
  };
}

function isOpen() {
  return visible.value === true;
}

async function close() {
  visible.value = undefined;
  form.value = null;
  schema.value = null;
  await syncFormRouteQuery({ section: null, step: null });

  if (props.afterClose) await props.afterClose();

  emit('close');
}

async function forwardFromQuestion(index) {
  processing.value = true;

  await validateAnswers();
  const submitted = index + 1 >= formQuestions.value.length;
  if (submitted) form.value.submitted = true;
  if (!form.value.provider_id) form.value.provider_id = props.providerId;

  let response = null;
  switch (role.value) {
    case 'manager':
      if (form.value.id) {
        response = await api.manager.provider.form.update(
          props.providerId,
          form.value.id,
          form.value,
        );
      } else {
        response = await api.manager.provider.form.create(props.providerId, form.value);
      }
      break;
    case 'specialist':
      if (!submitted) break;

      form.value.name = schema.value.name;
      response = await api.organization.form
        .create(form.value)
        .catch((error) => eventBus.error(error));
      break;
    case 'parent':
      if (!submitted && !familyFacing.value) break;

      if (form.value.id) {
        response = await api.parent.form.update(form.value.id, form.value);
      } else {
        response = await api.parent.form.create(form.value);
      }
      break;
    default:
      eventBus.error('Invalid role');
      return;
  }

  if (!form.value.id && response?.data) {
    form.value = response.data;
  }

  if (submitted) {
    processing.value = false;
    if (!response?.data) return;

    if (schema.value?.meta?.submitted_redirect_url) {
      window.location.replace(schema.value.meta.submitted_redirect_url);
      return;
    }

    if (props.afterSave) await props.afterSave(response.data);

    emit('save', response.data);
    await close();
  } else {
    section.value = null;
    setTimeout(() => {
      section.value = `question-${index + 1}`;
    }, 600);

    processing.value = false;
  }
}

async function loadAttachments() {
  const response = await api.member.attachment.index({
    owner_type: 'Form',
    owner_id: form.value.id,
  });
  if (response?.status !== 200) return;

  attachments.value = response.data;
}

async function open(newForm = {}) {
  await syncFormRouteQuery({ section: 'question-0', step: 1 });
  schema.value = store.state.schemas[props.schemaId];

  if (props.draft) {
    let response = null;

    switch (role.value) {
      case 'manager':
        if (newForm.id) {
          response = await api.manager.form.get(newForm.id);
        } else {
          response = await api.manager.provider.form.create(props.providerId, blankForm());
        }
        break;
      case 'parent':
        if (newForm.id) {
          response = await api.parent.form.get(newForm.id);
        } else {
          response = await api.parent.form
            .create(blankForm())
            .catch((error) => eventBus.error(error));
        }
        break;
      default:
        eventBus.error('Invalid role');
    }

    if (![200, 201].includes(response?.status)) return;

    form.value = response.data;

    // Deconstruct V2
    if (form.value.data) {
      form.value = {
        id: form.value.data.id,
        provider_id: form.value.data.relationships.provider.data.id,
        schema_id: form.value.data.relationships.schema.data.id,
        ...form.value.data.attributes,
      };
    }

    await loadAttachments();
  } else {
    form.value = blankForm();
  }

  await loadQuestions();
  section.value = 'question-0';
  visible.value = true;
}
</script>
